import React from 'react';
import { Shield, Mail, Phone } from 'lucide-react';
import { logoConfig } from '../../assets/logos';

interface AccessDeniedProps {
  userPrincipalName?: string | null;
  userGroups?: string[];
  onSignOut?: () => void;
  onRetry?: () => void;
}

/**
 * Professional access denied component for unauthorized users
 * Part of Phase C - M365 Group Authentication Authorization
 */
export const AccessDenied: React.FC<AccessDeniedProps> = ({
  userPrincipalName,
  userGroups = [],
  onSignOut,
  onRetry
}) => {
  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <div className="max-w-lg w-full space-y-8 p-8">
        
        {/* Header with logo */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <img 
              src={logoConfig.dark} 
              alt={logoConfig.alt}
              className="h-16 w-auto"
            />
          </div>
          <h1 className="text-2xl font-semibold text-white mb-2">Access Restricted</h1>
          <p className="text-gray-400">You don't have permission to access this application</p>
        </div>

        {/* Access denied icon and main message */}
        <div className="bg-gray-800 rounded-lg p-6 border border-red-900/50">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-red-900/20 p-3 rounded-full">
              <Shield className="w-8 h-8 text-red-400" />
            </div>
          </div>
          
          <div className="text-center">
            <h2 className="text-lg font-medium text-white mb-2">Authorization Required</h2>
            <p className="text-gray-300 text-sm leading-relaxed">
              This application requires membership in specific Microsoft 365 groups. 
              Please contact your administrator to request access.
            </p>
          </div>
        </div>

        {/* User information */}
        {userPrincipalName && (
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <h3 className="text-sm font-medium text-gray-300 mb-3">Account Information</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">Account:</span>
                <span className="text-sm text-white font-mono">{userPrincipalName}</span>
              </div>
              {userGroups.length > 0 && (
                <div className="pt-2 border-t border-gray-700">
                  <span className="text-sm text-gray-400 block mb-2">Current Groups:</span>
                  <div className="space-y-1">
                    {userGroups.slice(0, 3).map((group, index) => (
                      <span key={index} className="text-xs text-gray-500 bg-gray-700 px-2 py-1 rounded block">
                        {group}
                      </span>
                    ))}
                    {userGroups.length > 3 && (
                      <span className="text-xs text-gray-500">
                        ... and {userGroups.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Contact and actions */}
        <div className="space-y-4">
          
          {/* Contact information */}
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <h3 className="text-sm font-medium text-gray-300 mb-3">Need Access?</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center space-x-2 text-gray-400">
                <Mail className="w-4 h-4" />
                <span>Contact your IT administrator</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-400">
                <Phone className="w-4 h-4" />
                <span>Request access to the required security group</span>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex space-x-3">
            {onRetry && (
              <button
                onClick={onRetry}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200"
              >
                Check Again
              </button>
            )}
            {onSignOut && (
              <button
                onClick={onSignOut}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-gray-300 font-medium py-3 px-4 rounded-lg transition-colors duration-200"
              >
                Sign Out
              </button>
            )}
          </div>

          {/* Footer */}
          <div className="text-center">
            <p className="text-xs text-gray-500">
              Secure access control powered by Microsoft 365 • IntelligentHive
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
